/**
 * formatApiError — normalise the backend error envelope into the two strings a
 * toast renders: a bold `title` (the headline) and an optional `description`
 * (the sub-text).
 *
 * The backend returns `{ error, message, details }` where `details` is one of:
 *   - `null`                            → no sub-text (service-level message only)
 *   - a DRF field map `{ field: [..] }`  → flatten each field's messages
 *   - an options bag `{ allowed: [..] }` → render the list comma-joined
 *   - a *nested* serializer map, e.g.
 *     `{ events: [{ spaces: [{ setup_configuration: ["…"] }] }] }`
 *                                        → recurse to the leaf messages
 *
 * Two refinements keep the toast readable:
 *   1. **Headline promotion** — when `message` is a generic DRF headline
 *      ("Validation Error" / "Bad Request" / …), the real, actionable sentence
 *      lives in `details`; we promote it to the `title` so the user reads it
 *      first instead of a meaningless "Validation Error".
 *   2. **Id resolution** — the backend often echoes the offending record's raw
 *      UUID (e.g. a `space` id). An optional `resolveId` maps it to a human
 *      name; an unresolved UUID is dropped rather than shown raw.
 *
 * Keeping this pure (no toast/UI coupling) lets any caller turn a raw API error
 * into `showError(title, description)` — see AlertProvider's `showApiError`.
 */

/** The shape of the error payload the backend returns on a failed request. */
export interface ApiErrorPayload {
  /** Machine code, e.g. "validation_error" | "invalid_mime". */
  error?: string;
  /** Human headline, e.g. "Validation failed." */
  message?: string;
  /** Field/option detail map (possibly nested), or null when there's nothing extra. */
  details?: Record<string, unknown> | null;
}

/** Options that tune how the error is rendered. */
export interface FormatApiErrorOptions {
  /**
   * Resolve a raw id the backend echoed (e.g. a space UUID) to a human label.
   * Return `undefined` when the id isn't known — an unresolved UUID is then
   * omitted rather than shown raw.
   */
  resolveId?: (id: string) => string | undefined;
}

/** The two display strings a toast needs. */
export interface FormattedApiError {
  title: string;
  description?: string;
}

/** One flattened detail line. `rootKey` is the top-level DRF key it came from. */
interface DetailLine {
  rootKey: string;
  label: string;
  text: string;
}

/** Matches a bare UUID (v4-shaped) so we never surface a raw id in a toast. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Generic DRF headlines that carry no information — promote a detail line over these. */
const GENERIC_TITLES = new Set([
  "validation error",
  "validation failed",
  "bad request",
  "something went wrong",
  "error",
]);

/** Keys whose value is *the* human error message (promotable to the headline as-is). */
const PRIMARY_KEYS = new Set([
  "non_field_errors",
  "non_field_error",
  "__all__",
  "detail",
  "message",
  "error",
]);

/** Title-case a snake/space key for display: `phone_country_code` → `Phone country code`. */
function humanizeKey(key: string): string {
  const spaced = key.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Resolve/clean a single leaf value for display. Returns the human label when
 * `resolveId` knows the id, the trimmed text otherwise, or `null` when the value
 * is an unresolved raw UUID (dropped so it never reaches the toast).
 */
function displayLeaf(
  value: unknown,
  resolveId?: FormatApiErrorOptions["resolveId"],
): string | null {
  const text = String(value ?? "").trim();
  if (!text) return null;
  const resolved = resolveId?.(text);
  if (resolved) return resolved;
  if (UUID_RE.test(text)) return null;
  return text;
}

/**
 * Walk a detail value to its string leaves, pushing one line per unique message.
 * `label` is the nearest object key (the meaningful field name for DRF nested
 * serializers); `rootKey` is the originating top-level key (used for promotion).
 */
function collectLines(
  value: unknown,
  label: string,
  rootKey: string,
  resolveId: FormatApiErrorOptions["resolveId"],
  out: DetailLine[],
): void {
  if (Array.isArray(value)) {
    for (const item of value)
      collectLines(item, label, rootKey, resolveId, out);
    return;
  }
  if (value && typeof value === "object") {
    // Descend into each field; the field key becomes the next label.
    for (const [key, child] of Object.entries(value)) {
      collectLines(child, humanizeKey(key), rootKey, resolveId, out);
    }
    return;
  }
  // Leaf — clean/resolve it, then emit "<label>: <msg>", de-duplicated.
  const text = displayLeaf(value, resolveId);
  if (!text) return;
  if (!out.some((l) => l.label === label && l.text === text))
    out.push({ rootKey, label, text });
}

/** Pick the detail line that best serves as the headline when `message` is generic. */
function pickPrimaryIndex(lines: DetailLine[]): number {
  // Prefer a line from a "primary" DRF key (non_field_errors / detail / …).
  const byKey = lines.findIndex((l) => PRIMARY_KEYS.has(l.rootKey));
  if (byKey !== -1) return byKey;
  // Otherwise the most sentence-like line (has letters + a space; longest wins).
  let best = 0;
  let bestScore = -1;
  lines.forEach((l, i) => {
    const human = /[a-z]/i.test(l.text) && /\s/.test(l.text);
    const score = (human ? 1000 : 0) + l.text.length;
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  });
  return best;
}

/**
 * Map a backend error payload to `{ title, description }`.
 *
 * @param payload the raw `{ error, message, details }` from the API
 * @param options optional `resolveId` to turn echoed ids into human names
 * @returns the toast headline + (when `details` carries anything) sub-text
 */
export function formatApiError(
  payload: ApiErrorPayload | null | undefined,
  options?: FormatApiErrorOptions,
): FormattedApiError {
  const resolveId = options?.resolveId;
  const rawTitle = payload?.message?.trim() ?? "";

  const details = payload?.details;
  // `null`/missing details or a non-object → headline only.
  if (!details || typeof details !== "object") {
    return { title: rawTitle || "Something went wrong." };
  }

  // Flatten every detail into { rootKey, label, text } lines.
  const lines: DetailLine[] = [];
  for (const [key, value] of Object.entries(details)) {
    const label = humanizeKey(key);
    const rootKey = key.toLowerCase();
    // A flat array of primitives (the common field-error / options case) reads
    // best joined on one line; "allowed" is comma-joined, others space-joined.
    if (Array.isArray(value) && value.every((v) => typeof v !== "object")) {
      const parts = value
        .map((v) => displayLeaf(v, resolveId))
        .filter((v): v is string => Boolean(v));
      const text = parts.join(key === "allowed" ? ", " : " ").trim();
      if (text && !lines.some((l) => l.label === label && l.text === text)) {
        lines.push({ rootKey, label, text });
      }
      continue;
    }
    // Nested object/array → recurse to the leaf messages.
    collectLines(value, label, rootKey, resolveId, lines);
  }

  // Promote a real message to the headline when the backend's is generic
  // (trailing punctuation stripped so "Validation failed." also matches).
  const normalizedTitle = rawTitle.toLowerCase().replace(/[.!\s]+$/, "");
  const isGeneric = !rawTitle || GENERIC_TITLES.has(normalizedTitle);
  let title = rawTitle || "Something went wrong.";
  let bodyLines = lines;

  if (isGeneric && lines.length) {
    const idx = pickPrimaryIndex(lines);
    const primary = lines[idx];
    // A primary-key message (non_field_errors/detail/…) is a full sentence — use
    // it bare; a field-specific line keeps its "<Field>: <msg>" context.
    title = PRIMARY_KEYS.has(primary.rootKey)
      ? primary.text
      : `${primary.label}: ${primary.text}`;
    bodyLines = lines.filter((_, i) => i !== idx);
  }

  const description = bodyLines.length
    ? bodyLines.map((l) => `${l.label}: ${l.text}`).join("\n")
    : undefined;

  return { title, description };
}

export default formatApiError;
